import { PUBLIC_WEEKLY_AVAILABILITY_OPTIONS } from '../Anamnesis/AnamnesisRegister/publicAnamnese.constants';

export type FupAnswerFormat = 'legacy' | 'current';

export const FUP_INTRO_LINES = [
  'Minha metodologia segue uma periodização planejada para sua evolução. Precisamos apenas atualizar algumas informações para ajustar o treino à sua rotina e necessidades.',
] as const;

export const FUP_CLOSING_LINE =
  'Seguiremos nossa metodologia e periodização, fazendo os ajustes necessários para que vc continue evoluindo!';

export const FUP_TRAINING_LOCATION_OPTIONS = [
  { value: 'Academia', label: 'Academia' },
  { value: 'Academia de condomínio', label: 'Academia de condomínio' },
  { value: 'Casa', label: 'Casa' },
  { value: 'Estarei viajando e sem materiais', label: 'Estarei viajando e sem materiais' },
] as const;

export const FUP_AVAILABLE_TRAINING_TIME_OPTIONS = [
  '20 min',
  '30 min',
  '40 min',
  '50 min',
  '60 min',
  '75 min',
  '90 min',
].map((label) => ({ value: label, label })) as ReadonlyArray<{ value: string; label: string }>;

export const FUP_QUESTION_LABELS = {
  lastCycleFeedback:
    'Como foi seu treino nesse último mês? O que funcionou bem e o que não funcionou?',
  nextCycleAdjustments:
    'Tem algo que vc gostaria que ajustássemos para o próximo ciclo? Pode ser alguma dor ou desconforto, mais foco em algum grupamento, mudança de academia/local de treino ou qualquer outra necessidade.',
  weeklyAvailability: 'Qual será sua disponibilidade?',
  availableTrainingTime: 'Tempo por treino',
  additionalNotes: 'Tem alguma mudança importante que precisamos saber?',
  trainingLocation: 'Onde você vai treinar no próximo mês?',
  hasPainOrLimitation:
    'Está sentindo alguma dor, desconforto ou limitação que precisamos considerar?',
  painOrLimitationDetails: 'Detalhe o que e em quais movimentos que parecem gerar dor',
  legacyWeeklyAvailability: 'Qual será sua disponibilidade no próximo mês?',
  legacyAvailableTrainingTime: 'Tempo disponível para cada treino',
  legacyAdditionalNotes: 'Tem algum outro ponto que queira acrescentar ou deixar registrado?',
} as const;

export const FUP_AVAILABILITY_HINT = 'Frequência semanal e tempo por treino.';

export interface FupQuestionnaireAnswers {
  format: FupAnswerFormat;
  lastCycleFeedback: string;
  nextCycleAdjustments: string;
  weeklyAvailability: string;
  availableTrainingTime: string;
  additionalNotes: string;
  trainingLocation: string;
  hasPainOrLimitation: boolean | null;
  painOrLimitationDetails: string;
}

export const EMPTY_FUP_QUESTIONNAIRE: FupQuestionnaireAnswers = {
  format: 'current',
  lastCycleFeedback: '',
  nextCycleAdjustments: '',
  weeklyAvailability: '',
  availableTrainingTime: '',
  additionalNotes: '',
  trainingLocation: '',
  hasPainOrLimitation: null,
  painOrLimitationDetails: '',
};

export const EMPTY_LEGACY_FUP_QUESTIONNAIRE: FupQuestionnaireAnswers = {
  ...EMPTY_FUP_QUESTIONNAIRE,
  format: 'legacy',
};

export type FupQuestionnaireErrors = Partial<
  Record<keyof Omit<FupQuestionnaireAnswers, 'format'>, string>
>;

const WEEKLY_VALUES = new Set<string>(
  PUBLIC_WEEKLY_AVAILABILITY_OPTIONS.map((option) => option.value),
);
const TIME_VALUES = new Set<string>(
  FUP_AVAILABLE_TRAINING_TIME_OPTIONS.map((option) => option.value),
);
const LEGACY_TIME_VALUES = new Set<string>([
  ...FUP_AVAILABLE_TRAINING_TIME_OPTIONS.map((option) => option.value),
  '120 min',
]);
const LOCATION_VALUES = new Set<string>(
  FUP_TRAINING_LOCATION_OPTIONS.map((option) => option.value),
);

const requireText = (value: string, message: string): string | undefined => (
  value.trim() ? undefined : message
);

export const validateFupQuestionnaire = (
  answers: FupQuestionnaireAnswers,
): FupQuestionnaireErrors => {
  const errors: FupQuestionnaireErrors = {};

  if (answers.format === 'legacy') {
    const lastCycle = requireText(answers.lastCycleFeedback, 'Conte como foi o treino nesse último mês.');
    const adjustments = requireText(
      answers.nextCycleAdjustments,
      'Informe se há algum ajuste para o próximo ciclo.',
    );

    if (lastCycle) {
      errors.lastCycleFeedback = lastCycle;
    } else if (answers.lastCycleFeedback.trim().length < 5) {
      errors.lastCycleFeedback = 'Descreva com pelo menos 5 caracteres.';
    }

    if (adjustments) {
      errors.nextCycleAdjustments = adjustments;
    }

    if (!WEEKLY_VALUES.has(answers.weeklyAvailability.trim())) {
      errors.weeklyAvailability = 'Selecione a frequência semanal.';
    }

    if (!LEGACY_TIME_VALUES.has(answers.availableTrainingTime.trim())) {
      errors.availableTrainingTime = 'Selecione o tempo disponível para cada treino.';
    }

    return errors;
  }

  if (!LOCATION_VALUES.has(answers.trainingLocation.trim())) {
    errors.trainingLocation = 'Selecione onde você vai treinar.';
  }

  if (!WEEKLY_VALUES.has(answers.weeklyAvailability.trim())) {
    errors.weeklyAvailability = 'Selecione a frequência semanal.';
  }

  if (!TIME_VALUES.has(answers.availableTrainingTime.trim())) {
    errors.availableTrainingTime = 'Selecione o tempo por treino.';
  }

  if (answers.hasPainOrLimitation === null) {
    errors.hasPainOrLimitation = 'Informe se há dor, desconforto ou limitação.';
  } else if (answers.hasPainOrLimitation) {
    const details = requireText(
      answers.painOrLimitationDetails,
      'Detalhe a dor, desconforto ou limitação e os movimentos envolvidos.',
    );
    if (details) {
      errors.painOrLimitationDetails = details;
    }
  }

  return errors;
};

export const composeFupResponse = (answers: FupQuestionnaireAnswers): string => {
  if (answers.format === 'legacy') {
    const additionalNotes = answers.additionalNotes.trim();
    const sections = [
      `${FUP_QUESTION_LABELS.lastCycleFeedback}\n${answers.lastCycleFeedback.trim()}`,
      `${FUP_QUESTION_LABELS.nextCycleAdjustments}\n${answers.nextCycleAdjustments.trim()}`,
      `${FUP_QUESTION_LABELS.legacyWeeklyAvailability}\n${answers.weeklyAvailability.trim()}, ${answers.availableTrainingTime.trim()}`,
    ];

    if (additionalNotes) {
      sections.push(`${FUP_QUESTION_LABELS.legacyAdditionalNotes}\n${additionalNotes}`);
    }

    return sections.join('\n\n');
  }

  const painValue = answers.hasPainOrLimitation
    ? `Sim\n${answers.painOrLimitationDetails.trim()}`
    : 'Não';
  const sections = [
    `${FUP_QUESTION_LABELS.trainingLocation}\n${answers.trainingLocation.trim()}`,
    `${FUP_QUESTION_LABELS.weeklyAvailability}\n${answers.weeklyAvailability.trim()}, ${answers.availableTrainingTime.trim()}`,
    `${FUP_QUESTION_LABELS.hasPainOrLimitation}\n${painValue}`,
  ];

  const additionalNotes = answers.additionalNotes.trim();
  if (additionalNotes) {
    sections.push(`${FUP_QUESTION_LABELS.additionalNotes}\n${additionalNotes}`);
  }

  return sections.join('\n\n');
};

export const FUP_ANSWER_ROW_LABELS = {
  lastCycleFeedback: 'Como foi o treino',
  nextCycleAdjustments: 'Ajustes',
  weeklyAvailability: 'Disponibilidade',
  availableTrainingTime: 'Tempo do treino',
  additionalNotes: 'Mudança importante',
  legacyAdditionalNotes: 'Outro ponto',
  trainingLocation: 'Local de treino',
  hasPainOrLimitation: 'Dor ou limitação',
  painOrLimitationDetails: 'Detalhes da dor/limitação',
} as const;

export interface FupAnswerRow {
  label: string;
  value: string;
}

const LEGACY_QUESTIONNAIRE_SECTIONS: Array<{
  key: 'lastCycleFeedback' | 'nextCycleAdjustments' | 'weeklyAvailability' | 'additionalNotes';
  question: string;
}> = [
  { key: 'lastCycleFeedback', question: FUP_QUESTION_LABELS.lastCycleFeedback },
  { key: 'nextCycleAdjustments', question: FUP_QUESTION_LABELS.nextCycleAdjustments },
  { key: 'weeklyAvailability', question: FUP_QUESTION_LABELS.legacyWeeklyAvailability },
  { key: 'additionalNotes', question: FUP_QUESTION_LABELS.legacyAdditionalNotes },
];

const CURRENT_QUESTIONNAIRE_SECTIONS: Array<{
  key: 'trainingLocation' | 'weeklyAvailability' | 'hasPainOrLimitation' | 'additionalNotes';
  question: string;
}> = [
  { key: 'trainingLocation', question: FUP_QUESTION_LABELS.trainingLocation },
  { key: 'weeklyAvailability', question: FUP_QUESTION_LABELS.weeklyAvailability },
  { key: 'hasPainOrLimitation', question: FUP_QUESTION_LABELS.hasPainOrLimitation },
  { key: 'additionalNotes', question: FUP_QUESTION_LABELS.additionalNotes },
];

export const fupAnswersToRows = (answers: FupQuestionnaireAnswers): FupAnswerRow[] => {
  if (answers.format === 'legacy') {
    const rows: FupAnswerRow[] = [
      { label: FUP_ANSWER_ROW_LABELS.lastCycleFeedback, value: answers.lastCycleFeedback },
      { label: FUP_ANSWER_ROW_LABELS.nextCycleAdjustments, value: answers.nextCycleAdjustments },
      { label: FUP_ANSWER_ROW_LABELS.weeklyAvailability, value: answers.weeklyAvailability },
      { label: FUP_ANSWER_ROW_LABELS.availableTrainingTime, value: answers.availableTrainingTime },
    ];

    if (answers.additionalNotes.trim()) {
      rows.push({
        label: FUP_ANSWER_ROW_LABELS.legacyAdditionalNotes,
        value: answers.additionalNotes,
      });
    }

    return rows.filter((row) => row.value.trim().length > 0);
  }

  const rows: FupAnswerRow[] = [
    { label: FUP_ANSWER_ROW_LABELS.trainingLocation, value: answers.trainingLocation },
    { label: FUP_ANSWER_ROW_LABELS.weeklyAvailability, value: answers.weeklyAvailability },
    { label: FUP_ANSWER_ROW_LABELS.availableTrainingTime, value: answers.availableTrainingTime },
    {
      label: FUP_ANSWER_ROW_LABELS.hasPainOrLimitation,
      value: answers.hasPainOrLimitation === null
        ? ''
        : answers.hasPainOrLimitation
          ? 'Sim'
          : 'Não',
    },
  ];

  if (answers.hasPainOrLimitation && answers.painOrLimitationDetails.trim()) {
    rows.push({
      label: FUP_ANSWER_ROW_LABELS.painOrLimitationDetails,
      value: answers.painOrLimitationDetails,
    });
  }

  if (answers.additionalNotes.trim()) {
    rows.push({
      label: FUP_ANSWER_ROW_LABELS.additionalNotes,
      value: answers.additionalNotes,
    });
  }

  return rows.filter((row) => row.value.trim().length > 0);
};

const answersFromComposedResponse = (response: string): FupQuestionnaireAnswers => {
  const rows = parseFupResponseToRows(response);
  if (rows.length === 1 && rows[0]?.label === 'Resposta') {
    return {
      ...EMPTY_LEGACY_FUP_QUESTIONNAIRE,
      lastCycleFeedback: rows[0].value,
    };
  }

  const valueOf = (label: string): string => (
    rows.find((row) => row.label === label)?.value ?? ''
  );

  const isCurrent = rows.some((row) => row.label === FUP_ANSWER_ROW_LABELS.trainingLocation)
    || response.includes(FUP_QUESTION_LABELS.trainingLocation);

  if (isCurrent) {
    const painLabel = valueOf(FUP_ANSWER_ROW_LABELS.hasPainOrLimitation);
    const hasPain = painLabel.toLowerCase().startsWith('sim')
      ? true
      : painLabel.toLowerCase().startsWith('não') || painLabel.toLowerCase().startsWith('nao')
        ? false
        : null;

    return {
      ...EMPTY_FUP_QUESTIONNAIRE,
      format: 'current',
      trainingLocation: valueOf(FUP_ANSWER_ROW_LABELS.trainingLocation),
      weeklyAvailability: valueOf(FUP_ANSWER_ROW_LABELS.weeklyAvailability),
      availableTrainingTime: valueOf(FUP_ANSWER_ROW_LABELS.availableTrainingTime),
      hasPainOrLimitation: hasPain,
      painOrLimitationDetails: valueOf(FUP_ANSWER_ROW_LABELS.painOrLimitationDetails),
      additionalNotes: valueOf(FUP_ANSWER_ROW_LABELS.additionalNotes),
    };
  }

  return {
    ...EMPTY_LEGACY_FUP_QUESTIONNAIRE,
    lastCycleFeedback: valueOf(FUP_ANSWER_ROW_LABELS.lastCycleFeedback),
    nextCycleAdjustments: valueOf(FUP_ANSWER_ROW_LABELS.nextCycleAdjustments),
    weeklyAvailability: valueOf(FUP_ANSWER_ROW_LABELS.weeklyAvailability),
    availableTrainingTime: valueOf(FUP_ANSWER_ROW_LABELS.availableTrainingTime),
    additionalNotes: valueOf(FUP_ANSWER_ROW_LABELS.legacyAdditionalNotes)
      || valueOf(FUP_ANSWER_ROW_LABELS.additionalNotes),
  };
};

const hasFilledAnswers = (answers: Partial<FupQuestionnaireAnswers> | null | undefined): boolean => (
  Boolean(answers) && [
    answers?.lastCycleFeedback,
    answers?.nextCycleAdjustments,
    answers?.weeklyAvailability,
    answers?.availableTrainingTime,
    answers?.additionalNotes,
    answers?.trainingLocation,
    answers?.painOrLimitationDetails,
  ].some((value) => typeof value === 'string' && value.trim().length > 0)
  || typeof answers?.hasPainOrLimitation === 'boolean'
);

export const parseStoredFupAnswers = (
  answers?: Partial<FupQuestionnaireAnswers> | null,
  response?: string | null,
): FupQuestionnaireAnswers | null => {
  if (hasFilledAnswers(answers)) {
    const format: FupAnswerFormat = answers?.format === 'legacy'
      || (
        !answers?.format
        && !answers?.trainingLocation?.trim()
        && typeof answers?.hasPainOrLimitation !== 'boolean'
        && Boolean(answers?.lastCycleFeedback?.trim() || answers?.nextCycleAdjustments?.trim())
      )
      ? 'legacy'
      : 'current';

    if (format === 'legacy') {
      return {
        ...EMPTY_LEGACY_FUP_QUESTIONNAIRE,
        lastCycleFeedback: answers?.lastCycleFeedback?.trim() ?? '',
        nextCycleAdjustments: answers?.nextCycleAdjustments?.trim() ?? '',
        weeklyAvailability: answers?.weeklyAvailability?.trim() ?? '',
        availableTrainingTime: answers?.availableTrainingTime?.trim() ?? '',
        additionalNotes: answers?.additionalNotes?.trim() ?? '',
      };
    }

    return {
      ...EMPTY_FUP_QUESTIONNAIRE,
      trainingLocation: answers?.trainingLocation?.trim() ?? '',
      weeklyAvailability: answers?.weeklyAvailability?.trim() ?? '',
      availableTrainingTime: answers?.availableTrainingTime?.trim() ?? '',
      hasPainOrLimitation: typeof answers?.hasPainOrLimitation === 'boolean'
        ? answers.hasPainOrLimitation
        : null,
      painOrLimitationDetails: answers?.painOrLimitationDetails?.trim() ?? '',
      additionalNotes: answers?.additionalNotes?.trim() ?? '',
    };
  }

  const rawResponse = response?.trim() ?? '';
  if (!rawResponse) {
    return EMPTY_FUP_QUESTIONNAIRE;
  }

  return answersFromComposedResponse(rawResponse);
};

export const parseFupResponseToRows = (response?: string | null): FupAnswerRow[] => {
  const text = response?.trim() ?? '';
  if (!text) {
    return [];
  }

  const isCurrent = CURRENT_QUESTIONNAIRE_SECTIONS.some((section) => text.includes(section.question));
  const sections = isCurrent ? CURRENT_QUESTIONNAIRE_SECTIONS : LEGACY_QUESTIONNAIRE_SECTIONS;
  const found = sections.filter((section) => text.includes(section.question));

  if (found.length === 0) {
    return [{ label: 'Resposta', value: text }];
  }

  const rows: FupAnswerRow[] = [];

  found.forEach((section, index) => {
    const start = text.indexOf(section.question) + section.question.length;
    const nextQuestion = found[index + 1]?.question;
    const end = nextQuestion ? text.indexOf(nextQuestion, start) : text.length;
    const value = text.slice(start, end).trim();
    if (!value) {
      return;
    }

    if (section.key === 'weeklyAvailability') {
      const [weekly, ...timeParts] = value.split(',').map((part) => part.trim()).filter(Boolean);
      if (weekly) {
        rows.push({ label: FUP_ANSWER_ROW_LABELS.weeklyAvailability, value: weekly });
      }
      const time = timeParts.join(', ');
      if (time) {
        rows.push({ label: FUP_ANSWER_ROW_LABELS.availableTrainingTime, value: time });
      }
      return;
    }

    if (section.key === 'hasPainOrLimitation') {
      const [firstLine, ...rest] = value.split('\n').map((part) => part.trim()).filter(Boolean);
      const painLabel = firstLine ?? value;
      rows.push({ label: FUP_ANSWER_ROW_LABELS.hasPainOrLimitation, value: painLabel });
      const details = rest.join('\n').trim();
      if (details) {
        rows.push({ label: FUP_ANSWER_ROW_LABELS.painOrLimitationDetails, value: details });
      }
      return;
    }

    if (section.key === 'trainingLocation') {
      rows.push({ label: FUP_ANSWER_ROW_LABELS.trainingLocation, value });
      return;
    }

    if (section.key === 'lastCycleFeedback') {
      rows.push({ label: FUP_ANSWER_ROW_LABELS.lastCycleFeedback, value });
      return;
    }

    if (section.key === 'nextCycleAdjustments') {
      rows.push({ label: FUP_ANSWER_ROW_LABELS.nextCycleAdjustments, value });
      return;
    }

    rows.push({
      label: isCurrent
        ? FUP_ANSWER_ROW_LABELS.additionalNotes
        : FUP_ANSWER_ROW_LABELS.legacyAdditionalNotes,
      value,
    });
  });

  return rows.length > 0 ? rows : [{ label: 'Resposta', value: text }];
};
